import {
  Campaign,
  CreatorCriterion,
  EditCampaignData,
  EditCampaignDialogProps,
} from "./types";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ExternalLink,
  Info,
  Loader2,
  Plus,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { getTemplateBrief, getTemplateCreatorCriteria } from "./constants";
import { useCallback, useEffect, useRef, useState } from "react";
import { useConvex, useQuery } from "convex/react";
import { useConvexMutation, useConvexQuery } from "@convex-dev/react-query";

import { APPROVED_STRIPE_CONNECT_COUNTRIES } from "@/constants/approvedCountries";
import AiScrapingDialog from "./AiScrapingDialog";
import { Button } from "@/components/ui/button";
import { DEMO_IDS } from "@/mock/demo";
import { Id } from "@cvx/_generated/dataModel";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { Switch } from "@/components/ui/switch";
import { TEAM_USER_IDS } from "@/constants/userIds";
import { Textarea } from "@/components/ui/textarea";
import { TooltipProvider } from "@radix-ui/react-tooltip";
import { api } from "@cvx/_generated/api";
import { countries } from "countries-list";
import getUnicodeFlagIcon from "country-flag-icons/unicode";
import { useAccount } from "@/state/useAccount";
import { useMutation } from "@tanstack/react-query";
import { useToast } from "@/components/ui/use-toast";
import { useUploadFiles } from "@xixixao/uploadstuff/react";

// Extended campaign interface for missing properties
interface ExtendedCampaign extends Campaign {
  manualVideoReviewRequired?: boolean;
  teamVideoReviewRequired?: boolean;
  creatorCriteria?: CreatorCriterion[];
  prizePool?: number;
}

// Helper function to check if rich text content is empty
const isRichTextEmpty = (content: string): boolean => {
  if (!content) return true;
  // Remove HTML tags and check if there's actual text content
  const textContent = content.replace(/<[^>]*>/g, "").trim();
  return textContent === "";
};

export default function EditCampaignDialog({
  open,
  onOpenChange,
  campaign,
}: EditCampaignDialogProps) {
  // Cast campaign to extended interface to access additional properties
  const extendedCampaign = campaign as ExtendedCampaign;
  const { demoModeEnabled } = useAccount();

  // Get current user to check if they're a team member
  const currentUser = useQuery(api.app.getCurrentUser, {});
  const isTeamMember = currentUser
    ? TEAM_USER_IDS.includes(currentUser._id)
    : false;

  // Check if we're in demo mode
  const isDemo =
    demoModeEnabled &&
    (campaign.appId as unknown as string) ===
      (DEMO_IDS.appId as unknown as string);

  const [formData, setFormData] = useState<EditCampaignData>({
    campaignId: campaign._id,
    name: campaign.name,
    type: campaign.type,
    isPublic: campaign.isPublic,
    applicationNotRequired: campaign.applicationNotRequired,
    manualVideoReviewRequired:
      extendedCampaign.manualVideoReviewRequired || false,
    teamVideoReviewRequired: extendedCampaign.teamVideoReviewRequired ?? true, // Default to true
    aiReviewRequired: campaign.aiReviewRequired || false,
    allowCreatorsToPostDirectlyToSocials:
      campaign.allowCreatorsToPostDirectlyToSocials || false,
    allowedChannels: campaign.allowedChannels as (
      | "tiktok"
      | "youtube"
      | "youtube-shorts"
      | "instagram"
    )[],
    validationRequirements: campaign.validationRequirements || "",
    creatorBrief: campaign.creatorBrief || "",
    creatorCriteria: extendedCampaign.creatorCriteria,
    budget: extendedCampaign.prizePool,
    budgetCpmMin: campaign.budgetCpmMin,
    budgetCpmMax: campaign.budgetCpmMax,
    budgetFlatFeeMin: campaign.budgetFlatFeeMin,
    budgetFlatFeeMax: campaign.budgetFlatFeeMax,
    predefinedCpm: campaign.predefinedCpm,
    videos: campaign.videos || [], // Add existing videos
    urls: campaign.urls || [], // Add existing URLs
    problemsSolved: "", // Will be populated by useEffect when appAdditionalInfo loads
    customerTestimonials: [], // Will be populated by useEffect when appAdditionalInfo loads
    eligibleCountries:
      campaign.eligibleCountries || APPROVED_STRIPE_CONNECT_COUNTRIES, // Default to all approved countries if not set
  });
  const [endDateInput, setEndDateInput] = useState(
    campaign.endDate
      ? new Date(campaign.endDate).toISOString().split("T")[0]
      : "",
  );
  const [showEndCampaignDialog, setShowEndCampaignDialog] = useState(false);
  const [showCpmCalculator, setShowCpmCalculator] = useState(false);
  const [cpmCalculator, setCpmCalculator] = useState({
    lifetimeRevenue: "100",
    cacLtvRatio: "0.33",
    viewsToDownload: "0.25",
    downloadToPaid: "8",
  });

  // AI scraping state
  const [isAiScraping, setIsAiScraping] = useState(false);
  const [aiScrapingDialogOpen, setAiScrapingDialogOpen] = useState(false);
  const [aiScrapingDialogType, setAiScrapingDialogType] = useState<
    "usps" | "testimonials"
  >("usps");

  // Video management state
  const [uploadedVideos, setUploadedVideos] = useState<
    Array<{
      id: string;
      name: string;
      url?: string;
      isExisting?: boolean;
    }>
  >(() => {
    // Initialize with existing videos
    const existingVideos = campaign.videos || [];
    return existingVideos.map((video: string, index: number) => ({
      id: video,
      name: video.startsWith("http")
        ? video.includes("youtube.com") || video.includes("youtu.be")
          ? "YouTube Video"
          : video.includes("vimeo.com")
            ? "Vimeo Video"
            : "External Video"
        : `Campaign Video ${index + 1}`,
      url: video.startsWith("http") ? video : undefined,
      isExisting: true,
    }));
  });
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const videoUploadRef = useRef<HTMLInputElement>(null);

  // Campaign URL management state
  const [campaignUrls, setCampaignUrls] = useState<
    Array<{
      id: string;
      url: string;
      title: string;
      isExisting?: boolean;
    }>
  >(() => {
    // Initialize with existing URLs
    const existingUrls = campaign.urls || [];
    return existingUrls.map((url, index) => ({
      id: `existing-${index}`,
      title: url.title,
      url: url.url,
      isExisting: true,
    }));
  });

  // Inspiration Videos management state
  const [inspirationVideos, setInspirationVideos] = useState<
    Array<{
      id: string;
      name: string;
      url?: string;
      isExisting?: boolean;
    }>
  >(() => {
    // Initialize with existing inspiration videos
    const existingVideos = campaign.inspirationVideos || [];
    return existingVideos.map((video: string, index: number) => ({
      id: video,
      name: video.startsWith("http")
        ? video.includes("youtube.com") || video.includes("youtu.be")
          ? "YouTube Video"
          : video.includes("vimeo.com")
            ? "Vimeo Video"
            : video.includes("tiktok.com")
              ? "TikTok Video"
              : video.includes("instagram.com")
                ? "Instagram Video"
                : "External Video"
        : `Inspiration Video ${index + 1}`,
      url: video.startsWith("http") ? video : undefined,
      isExisting: true,
    }));
  });
  const [isUploadingInspirationVideo, setIsUploadingInspirationVideo] =
    useState(false);
  const inspirationVideoUploadRef = useRef<HTMLInputElement>(null);

  // Reset form data when dialog opens OR campaign changes
  useEffect(() => {
    setFormData({
      campaignId: campaign._id,
      name: campaign.name,
      type: campaign.type,
      isPublic: campaign.isPublic,
      applicationNotRequired: campaign.applicationNotRequired,
      manualVideoReviewRequired:
        extendedCampaign.manualVideoReviewRequired || false,
      teamVideoReviewRequired: extendedCampaign.teamVideoReviewRequired ?? true, // Default to true
      aiReviewRequired: campaign.aiReviewRequired || false,
      allowCreatorsToPostDirectlyToSocials:
        campaign.allowCreatorsToPostDirectlyToSocials || false,
      allowedChannels: campaign.allowedChannels as (
        | "tiktok"
        | "youtube"
        | "youtube-shorts"
        | "instagram"
      )[],
      validationRequirements: campaign.validationRequirements || "",
      creatorBrief: campaign.creatorBrief || "",
      creatorCriteria: extendedCampaign.creatorCriteria,
      budget: extendedCampaign.prizePool,
      budgetCpmMin: campaign.budgetCpmMin,
      budgetCpmMax: campaign.budgetCpmMax,
      budgetFlatFeeMin: campaign.budgetFlatFeeMin,
      budgetFlatFeeMax: campaign.budgetFlatFeeMax,
      predefinedCpm: campaign.predefinedCpm,
      videos: campaign.videos || [],
      urls: campaign.urls || [],
      problemsSolved: "", // Will be populated by useEffect when appAdditionalInfo loads
      customerTestimonials: [], // Will be populated by useEffect when appAdditionalInfo loads
      eligibleCountries:
        campaign.eligibleCountries || APPROVED_STRIPE_CONNECT_COUNTRIES,
    });

    setEndDateInput(
      campaign.endDate
        ? new Date(campaign.endDate).toISOString().split("T")[0]
        : "",
    );

    // Reset uploaded videos
    const existingVideos = campaign.videos || [];
    setUploadedVideos(
      existingVideos.map((video: string, index: number) => ({
        id: video,
        name: video.startsWith("http")
          ? video.includes("youtube.com") || video.includes("youtu.be")
            ? "YouTube Video"
            : video.includes("vimeo.com")
              ? "Vimeo Video"
              : "External Video"
          : `Campaign Video ${index + 1}`,
        url: video.startsWith("http") ? video : undefined,
        isExisting: true,
      })),
    );

    // Reset campaign URLs
    const existingUrls = campaign.urls || [];
    setCampaignUrls(
      existingUrls.map((url, index) => ({
        id: `existing-${index}`,
        title: url.title,
        url: url.url,
        isExisting: true,
      })),
    );

    // Reset inspiration videos
    const existingInspirationVideos = campaign.inspirationVideos || [];
    setInspirationVideos(
      existingInspirationVideos.map((video: string, index: number) => ({
        id: video,
        name: video.startsWith("http")
          ? video.includes("youtube.com") || video.includes("youtu.be")
            ? "YouTube Video"
            : video.includes("vimeo.com")
              ? "Vimeo Video"
              : video.includes("tiktok.com")
                ? "TikTok Video"
                : video.includes("instagram.com")
                  ? "Instagram Video"
                  : "External Video"
          : `Inspiration Video ${index + 1}`,
        url: video.startsWith("http") ? video : undefined,
        isExisting: true,
      })),
    );

    // Reset other dialog states
    setShowEndCampaignDialog(false);
    setIsUploadingVideo(false);
    setIsUploadingInspirationVideo(false);
  }, [campaign, open]);

  const { toast } = useToast();

  const appAdditionalInfo = useConvexQuery(
    api.campaigns.getAppAdditionalInfo,
    isDemo ? "skip" : { appId: campaign.appId },
  );

  // Fetch brand/app to display brand-specific team name
  const appInfo = useConvexQuery(api.apps.getApp, {
    appId: campaign.appId,
  });
  const brandName = appInfo?.name || "Brand";

  const editPermissions = useConvexQuery(
    api.campaigns.getCampaignEditPermissions,
    isDemo ? "skip" : { campaignId: campaign._id },
  );

  const updateAppAdditionalInfo = useConvexMutation(
    api.campaigns.updateAppAdditionalInfoFromForm,
  );

  const scrapeWebsite = useConvexMutation(api.campaigns.scrapeWebsiteForApp);
  const processFile = useConvexMutation(api.campaigns.processFileForApp);

  // Determine if restricted fields can be edited
  const canEditRestrictedFields =
    editPermissions?.canEditRestrictedFields ?? true;

  // Separate debounced auto-save for USPs and testimonials
  const uspsDebounceTimeoutRef = useRef<NodeJS.Timeout>();
  const testimonialsDebounceTimeoutRef = useRef<NodeJS.Timeout>();
  const [isAutoSaving, setIsAutoSaving] = useState(false);

  const debouncedSaveUSPs = useCallback(
    async (problemsSolved: string | undefined) => {
      if (uspsDebounceTimeoutRef.current) {
        clearTimeout(uspsDebounceTimeoutRef.current);
      }

      const currentCampaignId = campaign._id; // Capture current campaign ID at the time of calling
      uspsDebounceTimeoutRef.current = setTimeout(async () => {
        try {
          // Safety check: only save if campaign hasn't changed
          if (currentCampaignId !== campaign._id) {
            console.warn(
              "USPs auto-save cancelled: campaign changed during debounce",
            );
            return;
          }

          setIsAutoSaving(true);
          await updateAppAdditionalInfo({
            appId: campaign.appId,
            problemsSolved: problemsSolved || undefined,
            // Don't include testimonials - only save USPs
          });
        } catch (error) {
          console.warn("USPs auto-save failed:", error);
        } finally {
          setIsAutoSaving(false);
        }
      }, 2000); // 2 second debounce
    },
    [updateAppAdditionalInfo, campaign.appId, campaign._id],
  );

  const debouncedSaveTestimonials = useCallback(
    async (testimonials: typeof formData.customerTestimonials) => {
      if (testimonialsDebounceTimeoutRef.current) {
        clearTimeout(testimonialsDebounceTimeoutRef.current);
      }

      const currentCampaignId = campaign._id; // Capture current campaign ID at the time of calling
      testimonialsDebounceTimeoutRef.current = setTimeout(async () => {
        try {
          // Safety check: only save if campaign hasn't changed
          if (currentCampaignId !== campaign._id) {
            console.warn(
              "Testimonials auto-save cancelled: campaign changed during debounce",
            );
            return;
          }

          setIsAutoSaving(true);
          await updateAppAdditionalInfo({
            appId: campaign.appId,
            // Don't include problemsSolved - only save testimonials
            customerTestimonials:
              testimonials && testimonials.length > 0
                ? testimonials
                : undefined,
          });
        } catch (error) {
          console.warn("Testimonials auto-save failed:", error);
        } finally {
          setIsAutoSaving(false);
        }
      }, 2000); // 2 second debounce
    },
    [updateAppAdditionalInfo, campaign.appId, campaign._id],
  );

  // Auto-save USPs when problemsSolved changes
  useEffect(() => {
    if (formData.problemsSolved !== "") {
      debouncedSaveUSPs(formData.problemsSolved);
    }
  }, [formData.problemsSolved, debouncedSaveUSPs]);

  // Auto-save testimonials when customerTestimonials change
  useEffect(() => {
    if (
      formData.customerTestimonials &&
      formData.customerTestimonials.length > 0
    ) {
      debouncedSaveTestimonials(formData.customerTestimonials);
    }
  }, [formData.customerTestimonials, debouncedSaveTestimonials]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (uspsDebounceTimeoutRef.current) {
        clearTimeout(uspsDebounceTimeoutRef.current);
      }
      if (testimonialsDebounceTimeoutRef.current) {
        clearTimeout(testimonialsDebounceTimeoutRef.current);
      }
    };
  }, []);

  // Track the last known appAdditionalInfo and campaign to detect changes
  const lastAdditionalInfoRef = useRef<Array<{
    title: string;
    description: string;
    type: "usps" | "testimonial";
  }> | null>(null);
  const lastCampaignIdRef = useRef<string | null>(null);

  // Reset form data when campaign changes (switching campaigns)
  useEffect(() => {
    const currentCampaignId = campaign._id as unknown as string;

    // If campaign has changed, clear the additional info fields
    if (
      lastCampaignIdRef.current !== null &&
      lastCampaignIdRef.current !== currentCampaignId
    ) {
      // CRITICAL: Clear any pending auto-save timeouts to prevent saving old data to new campaign
      if (uspsDebounceTimeoutRef.current) {
        clearTimeout(uspsDebounceTimeoutRef.current);
        uspsDebounceTimeoutRef.current = undefined;
      }
      if (testimonialsDebounceTimeoutRef.current) {
        clearTimeout(testimonialsDebounceTimeoutRef.current);
        testimonialsDebounceTimeoutRef.current = undefined;
      }

      setFormData((prev) => ({
        ...prev,
        problemsSolved: "",
        customerTestimonials: [],
      }));

      // Clear the refs to ensure fresh data loading
      lastAdditionalInfoRef.current = null;
    }

    // Update the ref to track current campaign
    lastCampaignIdRef.current = currentCampaignId;
  }, [campaign._id]);

  // Reset additional info fields when dialog opens to ensure clean state
  useEffect(() => {
    if (open) {
      // Clear the additional info fields when dialog opens
      setFormData((prev) => ({
        ...prev,
        problemsSolved: "",
        customerTestimonials: [],
      }));

      // Clear the refs to ensure fresh data loading
      lastAdditionalInfoRef.current = null;
    }
  }, [open]);

  // Auto-populate form fields with app additionalInfo
  useEffect(() => {
    if (appAdditionalInfo && appAdditionalInfo.length > 0) {
      // Check if appAdditionalInfo has actually changed (not just a re-render)
      const hasChanged =
        JSON.stringify(lastAdditionalInfoRef.current) !==
        JSON.stringify(appAdditionalInfo);

      // Find USPs and populate the problemsSolved field
      const uspsItems = appAdditionalInfo.filter(
        (item) => item.type === "usps",
      );

      // Update USPs if: no existing content OR appAdditionalInfo changed
      if (uspsItems.length > 0 && (!formData.problemsSolved || hasChanged)) {
        const uspsContent = uspsItems
          .map((item) => {
            // Check if description already contains HTML formatting
            if (
              item.description.includes("<") &&
              item.description.includes(">")
            ) {
              return item.description; // Already formatted as HTML
            } else {
              // Fallback to simple HTML formatting if not already formatted
              return `<h3>${item.title}</h3><p>${item.description}</p>`;
            }
          })
          .join("");

        // Only update if the content is actually different
        if (uspsContent !== formData.problemsSolved) {
          setFormData((prev) => ({ ...prev, problemsSolved: uspsContent }));
        }
      }

      // Find testimonials and populate the customerTestimonials field
      const testimonialItems = appAdditionalInfo.filter(
        (item) => item.type === "testimonial",
      );

      // Update testimonials if: no existing content OR appAdditionalInfo changed
      if (
        testimonialItems.length > 0 &&
        (!formData.customerTestimonials ||
          formData.customerTestimonials.length === 0 ||
          hasChanged)
      ) {
        const testimonials = testimonialItems.map((item) => ({
          testimonial: item.description,
          customerName: item.title,
        }));

        // Only update if the testimonials are actually different
        const testimonialsChanged =
          JSON.stringify(testimonials) !==
          JSON.stringify(formData.customerTestimonials);
        if (testimonialsChanged) {
          setFormData((prev) => ({
            ...prev,
            customerTestimonials: testimonials,
          }));
        }
      }

      // Update the ref to track current data
      lastAdditionalInfoRef.current = appAdditionalInfo;
    } else if (appAdditionalInfo && appAdditionalInfo.length === 0) {
      // If appAdditionalInfo is empty array, clear the form fields
      const hasChanged =
        JSON.stringify(lastAdditionalInfoRef.current) !==
        JSON.stringify(appAdditionalInfo);

      if (hasChanged) {
        setFormData((prev) => ({
          ...prev,
          problemsSolved: "",
          customerTestimonials: [],
        }));
        lastAdditionalInfoRef.current = appAdditionalInfo;
      }
    }
  }, [
    appAdditionalInfo,
    formData.problemsSolved,
    formData.customerTestimonials,
  ]);

  const generateUploadUrl = useConvexMutation(api.app.generateUploadUrl);

  const { startUpload } = useUploadFiles(generateUploadUrl, {
    onUploadComplete: async (uploaded) => {
      // Process all uploaded files
      const newVideos = uploaded.map((upload) => {
        const uploadResponse = upload.response as { storageId: string };
        return {
          id: uploadResponse.storageId,
          name: upload.name,
          url: undefined,
          isExisting: false,
        };
      });

      const updatedVideos = [...uploadedVideos, ...newVideos];
      setUploadedVideos(updatedVideos);

      // Auto-save videos to backend immediately (preserve other existing campaign values)
      try {
        await updateCampaign({
          campaignId: formData.campaignId,
          appId: campaign.appId,
          name: campaign.name, // Use existing value, not form value
          type: campaign.type,
          isPublic: campaign.isPublic,
          applicationNotRequired: campaign.applicationNotRequired,
          allowedChannels: campaign.allowedChannels as
            | ("tiktok" | "youtube" | "instagram")[]
            | undefined,
          validationRequirements: campaign.validationRequirements,
          creatorBrief: campaign.creatorBrief,
          endDate: campaign.endDate,
          budgetCpmMin: campaign.budgetCpmMin,
          budgetCpmMax: campaign.budgetCpmMax,
          budgetFlatFeeMin: campaign.budgetFlatFeeMin,
          budgetFlatFeeMax: campaign.budgetFlatFeeMax,
          predefinedCpm: campaign.predefinedCpm,
          videos: updatedVideos.map((video) => video.url || video.id),
        });

        setIsUploadingVideo(false);
        toast({
          title: `Video${uploaded.length > 1 ? "s" : ""} uploaded and saved`,
          description:
            uploaded.length > 1
              ? `${uploaded.length} videos have been uploaded and saved automatically`
              : `${uploaded[0].name} has been uploaded and saved automatically`,
        });
      } catch (error) {
        setIsUploadingVideo(false);
        toast({
          title: "Upload successful, but save failed",
          description:
            "Videos were uploaded but couldn't be saved to the campaign",
          variant: "destructive",
        });
      }
    },
    onUploadError: () => {
      setIsUploadingVideo(false);
      toast({
        title: "Upload failed",
        description: "There was an error uploading your video(s)",
        variant: "destructive",
      });
    },
  });

  const handleVideoUpload = (files: File[]) => {
    if (files.length === 0) return;

    // File size limit: 100MB per file
    const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB in bytes
    const oversizedFiles = files.filter((file) => file.size > MAX_FILE_SIZE);

    if (oversizedFiles.length > 0) {
      const oversizedFileNames = oversizedFiles.map((f) => f.name).join(", ");
      toast({
        title: "File size too large",
        description: `The following files exceed 100MB limit: ${oversizedFileNames}. Please compress your videos or upload them to YouTube/Google Drive and add the link instead.`,
        variant: "destructive",
      });
      return;
    }

    // Check total size of all files
    const totalSize = files.reduce((sum, file) => sum + file.size, 0);
    const MAX_TOTAL_SIZE = 200 * 1024 * 1024; // 200MB total

    if (totalSize > MAX_TOTAL_SIZE) {
      toast({
        title: "Total file size too large",
        description: `Total file size exceeds 200MB limit. Please upload fewer files or compress your videos first.`,
        variant: "destructive",
      });
      return;
    }

    setIsUploadingVideo(true);
    startUpload(files);
  };

  // Campaign URL management functions
  const addCampaignUrl = () => {
    const urlId = Date.now().toString();
    setCampaignUrls((prev) => [
      ...prev,
      {
        id: urlId,
        title: "",
        url: "",
        isExisting: false,
      },
    ]);
  };

  const removeCampaignUrl = (urlId: string) => {
    setCampaignUrls((prev) => prev.filter((url) => url.id !== urlId));
  };

  const updateCampaignUrl = (
    urlId: string,
    field: "title" | "url",
    value: string,
  ) => {
    setCampaignUrls((prev) =>
      prev.map((url) => (url.id === urlId ? { ...url, [field]: value } : url)),
    );
  };

  const removeInspirationVideo = async (videoId: string) => {
    try {
      // Use the same mutation to properly delete from storage if it's a storage ID
      await deleteCampaignVideo({
        campaignId: formData.campaignId,
        videoId: videoId,
        isInspirationVideo: true,
      });

      // Update local state
      const updatedVideos = inspirationVideos.filter(
        (video) => video.id !== videoId,
      );
      setInspirationVideos(updatedVideos);

      toast({
        title: "Inspiration video removed",
        description: "Video has been removed from the campaign.",
      });
    } catch (error) {
      toast({
        title: "Remove failed",
        description: "Couldn't remove inspiration video from the campaign",
        variant: "destructive",
      });
    }
  };

  const updateInspirationVideo = (videoId: string, value: string) => {
    setInspirationVideos((prev) =>
      prev.map((video) =>
        video.id === videoId ? { ...video, url: value, name: value } : video,
      ),
    );
  };

  const handleInspirationVideoUpload = (files: File[]) => {
    if (files.length === 0) return;

    // File size limit: 100MB per file
    const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB in bytes
    const oversizedFiles = files.filter((file) => file.size > MAX_FILE_SIZE);

    if (oversizedFiles.length > 0) {
      const oversizedFileNames = oversizedFiles.map((f) => f.name).join(", ");
      toast({
        title: "File size too large",
        description: `The following files exceed 100MB limit: ${oversizedFileNames}. Please compress your videos or upload them to YouTube/TikTok/Instagram and add the link instead.`,
        variant: "destructive",
      });
      return;
    }

    // Check total size of all files
    const totalSize = files.reduce((sum, file) => sum + file.size, 0);
    const MAX_TOTAL_SIZE = 200 * 1024 * 1024; // 200MB total

    if (totalSize > MAX_TOTAL_SIZE) {
      toast({
        title: "Total file size too large",
        description: `Total file size exceeds 200MB limit. Please upload fewer files or compress your videos first.`,
        variant: "destructive",
      });
      return;
    }

    setIsUploadingInspirationVideo(true);
    startUploadInspirationVideos(files);
  };

  const { startUpload: startUploadInspirationVideos } = useUploadFiles(
    generateUploadUrl,
    {
      onUploadComplete: async (uploaded) => {
        // Process all uploaded files
        const newVideos = uploaded.map((upload) => {
          const uploadResponse = upload.response as { storageId: string };
          return {
            id: uploadResponse.storageId,
            name: upload.name,
            url: undefined,
            isExisting: false,
          };
        });

        const updatedVideos = [...inspirationVideos, ...newVideos];
        setInspirationVideos(updatedVideos);

        // Auto-save inspiration videos to backend immediately
        try {
          await updateCampaign({
            campaignId: formData.campaignId,
            appId: campaign.appId,
            name: campaign.name,
            type: campaign.type,
            isPublic: campaign.isPublic,
            applicationNotRequired: campaign.applicationNotRequired,
            allowedChannels: campaign.allowedChannels as
              | ("tiktok" | "youtube" | "instagram")[]
              | undefined,
            validationRequirements: campaign.validationRequirements,
            creatorBrief: campaign.creatorBrief,
            endDate: campaign.endDate,
            budgetCpmMin: campaign.budgetCpmMin,
            budgetCpmMax: campaign.budgetCpmMax,
            budgetFlatFeeMin: campaign.budgetFlatFeeMin,
            budgetFlatFeeMax: campaign.budgetFlatFeeMax,
            predefinedCpm: campaign.predefinedCpm,
            inspirationVideos: updatedVideos.map(
              (video) => video.url || video.id,
            ),
          });

          setIsUploadingInspirationVideo(false);
          toast({
            title: `Inspiration video${uploaded.length > 1 ? "s" : ""} uploaded and saved`,
            description:
              uploaded.length > 1
                ? `${uploaded.length} videos have been uploaded and saved automatically`
                : `${uploaded[0].name} has been uploaded and saved automatically`,
          });
        } catch (error) {
          setIsUploadingInspirationVideo(false);
          toast({
            title: "Upload successful, but save failed",
            description:
              "Videos were uploaded but couldn't be saved to the campaign",
            variant: "destructive",
          });
        }
      },
      onUploadError: () => {
        setIsUploadingInspirationVideo(false);
        toast({
          title: "Upload failed",
          description: "There was an error uploading your video(s)",
          variant: "destructive",
        });
      },
    },
  );

  const addInspirationVideoUrl = async () => {
    const url = prompt("Enter inspiration video URL:");
    if (url && url.trim()) {
      const videoId = Date.now().toString();
      const newVideo = {
        id: videoId,
        name:
          url.includes("youtube.com") || url.includes("youtu.be")
            ? "YouTube Video"
            : url.includes("vimeo.com")
              ? "Vimeo Video"
              : url.includes("tiktok.com")
                ? "TikTok Video"
                : url.includes("instagram.com")
                  ? "Instagram Video"
                  : "External Video",
        url: url.trim(),
        isExisting: false,
      };

      const updatedVideos = [...inspirationVideos, newVideo];
      setInspirationVideos(updatedVideos);

      // Auto-save inspiration videos to backend immediately
      try {
        await updateCampaign({
          campaignId: formData.campaignId,
          appId: campaign.appId,
          name: campaign.name,
          type: campaign.type,
          isPublic: campaign.isPublic,
          applicationNotRequired: campaign.applicationNotRequired,
          allowedChannels: campaign.allowedChannels as
            | ("tiktok" | "youtube" | "instagram")[]
            | undefined,
          validationRequirements: campaign.validationRequirements,
          creatorBrief: campaign.creatorBrief,
          endDate: campaign.endDate,
          budgetCpmMin: campaign.budgetCpmMin,
          budgetCpmMax: campaign.budgetCpmMax,
          budgetFlatFeeMin: campaign.budgetFlatFeeMin,
          budgetFlatFeeMax: campaign.budgetFlatFeeMax,
          predefinedCpm: campaign.predefinedCpm,
          inspirationVideos: updatedVideos.map(
            (video) => video.url || video.id,
          ),
        });

        toast({
          title: "Inspiration video URL added and saved",
          description: "Video URL has been added to the campaign automatically",
        });
      } catch (error) {
        toast({
          title: "Add failed",
          description: "Couldn't add inspiration video URL to the campaign",
          variant: "destructive",
        });
        // Revert the state change on error
        setInspirationVideos(inspirationVideos);
      }
    }
  };

  // CPM Calculator functions
  const calculateRecommendedCpm = () => {
    const { lifetimeRevenue, cacLtvRatio, viewsToDownload, downloadToPaid } =
      cpmCalculator;

    const ltv = parseFloat(lifetimeRevenue) || 0;
    const cacRatio = parseFloat(cacLtvRatio) || 0;
    const viewsConversion = parseFloat(viewsToDownload) || 0;
    const downloadConversion = parseFloat(downloadToPaid) || 0;

    return (
      1000 *
      (viewsConversion / 100) *
      (downloadConversion / 100) *
      (ltv * cacRatio)
    );
  };

  // Creator criteria management functions
  const addCriterion = () => {
    const newCriterion: CreatorCriterion = {
      title: "",
      description: "",
      weight: undefined,
    };
    setFormData({
      ...formData,
      creatorCriteria: [...(formData.creatorCriteria || []), newCriterion],
    });
  };

  const removeCriterion = (index: number) => {
    const updatedCriteria = formData.creatorCriteria?.filter(
      (_, i) => i !== index,
    );
    setFormData({
      ...formData,
      creatorCriteria: updatedCriteria,
    });
  };

  const updateCriterion = (
    index: number,
    field: keyof CreatorCriterion,
    value: string | number,
  ) => {
    const updatedCriteria = formData.creatorCriteria?.map((criterion, i) =>
      i === index ? { ...criterion, [field]: value } : criterion,
    );
    setFormData({
      ...formData,
      creatorCriteria: updatedCriteria,
    });
  };

  const useTemplateCriteria = () => {
    setFormData({
      ...formData,
      creatorCriteria: getTemplateCreatorCriteria(),
    });
  };

  // Testimonial management functions
  const addTestimonial = () => {
    const updatedTestimonials = [
      ...(formData.customerTestimonials || []),
      { testimonial: "", customerName: "" },
    ];
    setFormData({
      ...formData,
      customerTestimonials: updatedTestimonials,
    });

    // Trigger immediate auto-save when testimonial is added
    debouncedSaveTestimonials(updatedTestimonials);
  };

  const removeTestimonial = (index: number) => {
    const updatedTestimonials = formData.customerTestimonials?.filter(
      (_, i) => i !== index,
    );
    const newFormData = {
      ...formData,
      customerTestimonials: updatedTestimonials,
    };
    setFormData(newFormData);

    // Trigger immediate auto-save when testimonial is removed
    debouncedSaveTestimonials(updatedTestimonials);
  };

  const updateTestimonial = (
    index: number,
    field: "testimonial" | "customerName",
    value: string,
  ) => {
    const updatedTestimonials = formData.customerTestimonials?.map((item, i) =>
      i === index ? { ...item, [field]: value } : item,
    );
    setFormData({
      ...formData,
      customerTestimonials: updatedTestimonials,
    });
  };

  // AI scraping function
  const handleAiScraping = async (data: { url?: string; file?: File }) => {
    if (!campaign.appId) {
      toast({
        title: "Error",
        description: "App ID is required for processing.",
        variant: "destructive",
      });
      return;
    }

    setIsAiScraping(true);
    try {
      if (data.url) {
        // Handle URL scraping
        await scrapeWebsite({
          websiteUrl: data.url.trim(),
          type:
            aiScrapingDialogType === "testimonials" ? "testimonial" : "usps",
          appId: campaign.appId,
        });

        toast({
          title: "AI Analysis Started",
          description: `AI is analyzing your website for ${aiScrapingDialogType}. This may take a few minutes.`,
        });
      } else if (data.file) {
        // Handle file upload and processing
        const uploadedFiles = await startUpload([data.file]);
        if (uploadedFiles.length > 0) {
          const uploadResponse = uploadedFiles[0].response as {
            storageId: string;
          };

          await processFile({
            fileId: uploadResponse.storageId as Id<"_storage">,
            type:
              aiScrapingDialogType === "testimonials" ? "testimonial" : "usps",
            appId: campaign.appId,
          });

          toast({
            title: "AI Analysis Started",
            description: `AI is analyzing your document for ${aiScrapingDialogType}. This may take a few minutes.`,
          });
        }
      }

      setAiScrapingDialogOpen(false);
    } catch (error) {
      toast({
        title: "Processing Failed",
        description: "Failed to start AI analysis. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsAiScraping(false);
    }
  };

  // Helper function to determine if applications are required
  const requiresApplications = () => {
    if (formData.type === "challenge") {
      return formData.isPublic && !formData.applicationNotRequired;
    }
    return formData.isPublic; // For CiR and influencer campaigns, public means applications are accepted
  };

  const { mutateAsync: updateCampaign, isPending: isUpdating } = useMutation({
    mutationFn: useConvexMutation(api.campaigns.createUpdateCampaign),
  });

  const { mutateAsync: endCampaign, isPending: isEnding } = useMutation({
    mutationFn: useConvexMutation(api.campaigns.endCampaign),
  });

  const { mutateAsync: deleteCampaignVideo } = useMutation({
    mutationFn: useConvexMutation(api.campaigns.deleteCampaignVideo),
  });

  const convex = useConvex();

  const viewVideo = async (videoId: string, videoUrl?: string) => {
    try {
      if (videoUrl) {
        // It's an external URL, open directly
        window.open(videoUrl, "_blank");
      } else {
        // It's a storage ID, get the URL from the backend
        const url = await convex.query(
          api.creator_internal_videos.getAttachmentUrl,
          {
            storageId: videoId as Id<"_storage">,
          },
        );

        if (url) {
          window.open(url, "_blank");
        } else {
          toast({
            title: "Unable to view video",
            description: "Could not get video URL",
            variant: "destructive",
          });
        }
      }
    } catch (error) {
      toast({
        title: "Error opening video",
        description: "There was an error trying to view the video",
        variant: "destructive",
      });
    }
  };

  const removeVideo = async (videoId: string) => {
    try {
      // Use the new mutation to properly delete from storage
      await deleteCampaignVideo({
        campaignId: formData.campaignId,
        videoId: videoId,
      });

      // Update local state
      const updatedVideos = uploadedVideos.filter(
        (video) => video.id !== videoId,
      );
      setUploadedVideos(updatedVideos);

      toast({
        title: "Video removed and deleted",
        description: "Video has been removed from the campaign.",
      });
    } catch (error) {
      toast({
        title: "Remove failed",
        description: "Couldn't remove video from the campaign",
        variant: "destructive",
      });
    }
  };

  const addVideoUrl = async () => {
    const url = prompt("Enter video URL:");
    if (url && url.trim()) {
      const videoId = Date.now().toString();
      const newVideo = {
        id: videoId,
        name:
          url.includes("youtube.com") || url.includes("youtu.be")
            ? "YouTube Video"
            : url.includes("vimeo.com")
              ? "Vimeo Video"
              : "External Video",
        url: url.trim(),
        isExisting: false,
      };

      const updatedVideos = [...uploadedVideos, newVideo];
      setUploadedVideos(updatedVideos);

      // Auto-save videos to backend immediately (preserve other existing campaign values)
      try {
        await updateCampaign({
          campaignId: formData.campaignId,
          appId: campaign.appId,
          name: campaign.name, // Use existing value, not form value
          type: campaign.type,
          isPublic: campaign.isPublic,
          applicationNotRequired: campaign.applicationNotRequired,
          allowedChannels: campaign.allowedChannels as
            | ("tiktok" | "youtube" | "instagram")[]
            | undefined,
          validationRequirements: campaign.validationRequirements,
          creatorBrief: campaign.creatorBrief,
          endDate: campaign.endDate,
          budgetCpmMin: campaign.budgetCpmMin,
          budgetCpmMax: campaign.budgetCpmMax,
          budgetFlatFeeMin: campaign.budgetFlatFeeMin,
          budgetFlatFeeMax: campaign.budgetFlatFeeMax,
          predefinedCpm: campaign.predefinedCpm,
          videos: updatedVideos.map((video) => video.url || video.id),
        });

        toast({
          title: "Video URL added and saved",
          description: "Video URL has been added to the campaign automatically",
        });
      } catch (error) {
        toast({
          title: "Add failed",
          description: "Couldn't add video URL to the campaign",
          variant: "destructive",
        });
        // Revert the state change on error
        setUploadedVideos(uploadedVideos);
      }
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast({
        title: "Error",
        description: "Campaign name is required.",
        variant: "destructive",
      });
      return;
    }

    // Challenge campaign specific validations
    if (formData.type === "challenge") {
      if (!formData.predefinedCpm || formData.predefinedCpm <= 0) {
        toast({
          title: "Error",
          description: "CPM reward is required for challenge campaigns.",
          variant: "destructive",
        });
        return;
      }

      if (!formData.validationRequirements?.trim()) {
        toast({
          title: "Error",
          description:
            "Post validation requirements are required for challenge campaigns.",
          variant: "destructive",
        });
        return;
      }
    }

    // Validate budget ranges if provided
    if (
      formData.budgetCpmMin !== undefined &&
      formData.budgetCpmMax !== undefined &&
      formData.budgetCpmMin > formData.budgetCpmMax
    ) {
      toast({
        title: "Error",
        description:
          "Minimum CPM budget cannot be higher than maximum CPM budget.",
        variant: "destructive",
      });
      return;
    }

    if (
      formData.budgetFlatFeeMin !== undefined &&
      formData.budgetFlatFeeMax !== undefined &&
      formData.budgetFlatFeeMin > formData.budgetFlatFeeMax
    ) {
      toast({
        title: "Error",
        description:
          "Minimum flat fee budget cannot be higher than maximum flat fee budget.",
        variant: "destructive",
      });
      return;
    }

    try {
      const campaignData = {
        campaignId: formData.campaignId,
        appId: campaign.appId,
        name: formData.name.trim(),
        type: formData.type,
        isPublic: formData.isPublic,
        applicationNotRequired: formData.applicationNotRequired,
        manualVideoReviewRequired: formData.manualVideoReviewRequired,
        teamVideoReviewRequired: formData.teamVideoReviewRequired,
        aiReviewRequired: formData.aiReviewRequired,
        allowCreatorsToPostDirectlyToSocials:
          formData.allowCreatorsToPostDirectlyToSocials,
        allowedChannels: formData.allowedChannels,
        validationRequirements: formData.validationRequirements,
        creatorBrief: formData.creatorBrief?.trim() || undefined,
        creatorCriteria: formData.creatorCriteria || undefined,
        endDate: endDateInput ? new Date(endDateInput).getTime() : undefined,
        prizePool: formData.budget, // Map budget to prizePool
        budgetCpmMin: formData.budgetCpmMin,
        budgetCpmMax: formData.budgetCpmMax,
        budgetFlatFeeMin: formData.budgetFlatFeeMin,
        budgetFlatFeeMax: formData.budgetFlatFeeMax,
        predefinedCpm: formData.predefinedCpm,
        // Videos are auto-saved, so we don't need to include them in manual updates
        // videos field will be preserved as-is from the last auto-save
        urls: campaignUrls.map((url) => ({
          url: url.url,
          title: url.title,
        })),
        inspirationVideos: inspirationVideos.map(
          (video) => video.url || video.id,
        ),
        eligibleCountries: formData.eligibleCountries,
      };

      await updateCampaign(campaignData);

      // Save additional info to the app (not the campaign)
      const shouldUpdateAppInfo =
        (formData.problemsSolved &&
          !isRichTextEmpty(formData.problemsSolved)) ||
        (formData.customerTestimonials &&
          formData.customerTestimonials.length > 0 &&
          formData.customerTestimonials.some(
            (t) => !isRichTextEmpty(t.testimonial) && t.customerName.trim(),
          ));

      if (shouldUpdateAppInfo) {
        await updateAppAdditionalInfo({
          appId: campaign.appId,
          problemsSolved:
            formData.problemsSolved && !isRichTextEmpty(formData.problemsSolved)
              ? formData.problemsSolved
              : undefined,
          customerTestimonials:
            formData.customerTestimonials &&
            formData.customerTestimonials.length > 0
              ? formData.customerTestimonials.filter(
                  (t) =>
                    !isRichTextEmpty(t.testimonial) && t.customerName.trim(),
                )
              : undefined,
        });
      }

      toast({
        title: "Campaign updated!",
        description: `${formData.name} has been updated successfully.`,
      });

      onOpenChange(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update campaign. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleEndCampaign = async () => {
    try {
      await endCampaign({ campaignId: campaign._id });

      toast({
        title: "Campaign ended!",
        description: `${campaign.name} has been ended successfully.`,
      });

      setShowEndCampaignDialog(false);
      onOpenChange(false);
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to end campaign. Please try again.";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Campaign</DialogTitle>
            <DialogDescription>
              Update your campaign details or end the campaign.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleUpdate} className="space-y-6">
            {/* Campaign Name */}
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm font-medium">
                Campaign Name *
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="Enter campaign name"
                required
              />
            </div>

            {/* Marketing Videos Section */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  Marketing Videos (Optional)
                </Label>
                <p className="text-xs text-muted-foreground">
                  Manage videos that help creators understand your app - app
                  demos, founder introductions, explainer videos, etc.
                  <strong>File limit: 100MB per video, 200MB total.</strong>
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border p-6">
                  {isUploadingVideo ? (
                    <>
                      <Loader2 className="mb-2 h-8 w-8 animate-spin text-primary/60" />
                      <p className="mb-1 text-sm font-medium">
                        Uploading video...
                      </p>
                      <p className="text-xs text-primary/60">
                        Please wait while we process your video
                      </p>
                    </>
                  ) : (
                    <>
                      <Upload className="mb-2 h-8 w-8 text-primary/60" />
                      <p className="mb-1 text-sm font-medium">
                        Add more marketing videos
                      </p>
                      <p className="mb-4 text-xs text-primary/60">
                        Drag and drop or click to select video files (multiple
                        files supported). Max 100MB per file, 200MB total.
                      </p>
                      <div className="flex gap-2">
                        <input
                          ref={videoUploadRef}
                          type="file"
                          accept="video/*"
                          multiple
                          className="hidden"
                          onChange={(e) => {
                            const files = Array.from(e.target.files || []);
                            handleVideoUpload(files);
                          }}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => videoUploadRef.current?.click()}
                          disabled={isUploadingVideo}
                        >
                          Upload Videos
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={addVideoUrl}
                          disabled={isUploadingVideo}
                        >
                          Add Video URL
                        </Button>
                      </div>
                    </>
                  )}
                </div>

                {/* Display uploaded videos */}
                {uploadedVideos.length > 0 && (
                  <div className="space-y-2">
                    <h5 className="text-sm font-medium">Marketing Videos:</h5>
                    {uploadedVideos.map((video) => (
                      <div
                        key={video.id}
                        className="flex items-center justify-between p-3 bg-muted rounded-lg"
                      >
                        <div
                          className="flex items-center gap-2 cursor-pointer hover:bg-muted/80 rounded p-1 -m-1 flex-1"
                          onClick={() => viewVideo(video.id, video.url)}
                        >
                          <div className="h-8 w-8 bg-blue-100 dark:bg-blue-900/20 rounded flex items-center justify-center">
                            <span className="text-xs text-blue-600 dark:text-blue-400">
                              📹
                            </span>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium">
                                {video.name}
                              </span>
                              <ExternalLink className="h-3 w-3 text-muted-foreground" />
                            </div>
                            {video.url && (
                              <p className="text-xs text-muted-foreground">
                                External URL
                              </p>
                            )}
                            {video.isExisting && (
                              <p className="text-xs text-green-600 dark:text-green-400">
                                Current video
                              </p>
                            )}
                            <p className="text-xs text-blue-600 dark:text-blue-400">
                              Click to view video
                            </p>
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeVideo(video.id)}
                          className="text-red-500 hover:text-red-700 ml-2"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Campaign URLs Section */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  Useful Links (Optional)
                </Label>
                <p className="text-xs text-muted-foreground">
                  Add helpful links that creators can access - TestFlight links,
                  demo pages, documentation, link to drive with b-roll footage,
                  etc.
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex justify-center">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={addCampaignUrl}
                    className="flex items-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Add Link
                  </Button>
                </div>

                {/* Display campaign URLs */}
                {campaignUrls.length > 0 && (
                  <div className="space-y-2">
                    <h5 className="text-sm font-medium">Useful Links:</h5>
                    {campaignUrls.map((urlItem) => (
                      <div
                        key={urlItem.id}
                        className="flex items-center justify-between p-3 bg-muted rounded-lg"
                      >
                        <div className="flex items-center gap-2 flex-1">
                          <div className="h-8 w-8 bg-green-100 dark:bg-green-900/20 rounded flex items-center justify-center">
                            <span className="text-xs text-green-600 dark:text-green-400">
                              🔗
                            </span>
                          </div>
                          <div className="flex-1 space-y-1">
                            <Input
                              value={urlItem.title}
                              onChange={(e) =>
                                updateCampaignUrl(
                                  urlItem.id,
                                  "title",
                                  e.target.value,
                                )
                              }
                              placeholder="Link title"
                              className="text-sm font-medium"
                            />
                            <Input
                              value={urlItem.url}
                              onChange={(e) =>
                                updateCampaignUrl(
                                  urlItem.id,
                                  "url",
                                  e.target.value,
                                )
                              }
                              placeholder="https://..."
                              className="text-xs"
                            />
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeCampaignUrl(urlItem.id)}
                          className="text-red-500 hover:text-red-700 ml-2"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Past Successful UGC Videos Section */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  Past Successful UGC Videos (Optional)
                </Label>
                <p className="text-xs text-muted-foreground">
                  Upload videos or add links to past successful user-generated
                  content videos to inspire creators. These will be shown to
                  creators as examples of great content.{" "}
                  <strong>File limit: 100MB per video, 200MB total.</strong>
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border p-6">
                  {isUploadingInspirationVideo ? (
                    <>
                      <Loader2 className="mb-2 h-8 w-8 animate-spin text-primary/60" />
                      <p className="mb-1 text-sm font-medium">
                        Uploading video...
                      </p>
                      <p className="text-xs text-primary/60">
                        Please wait while we process your video
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="mb-1 text-sm font-medium">
                        Add inspiration videos
                      </p>
                      <p className="mb-4 text-xs text-primary/60">
                        Upload video files or add external links (YouTube,
                        TikTok, Instagram). Max 100MB per file, 200MB total.
                      </p>
                      <div className="flex gap-2">
                        <input
                          ref={inspirationVideoUploadRef}
                          type="file"
                          accept="video/*"
                          multiple
                          className="hidden"
                          onChange={(e) => {
                            const files = Array.from(e.target.files || []);
                            handleInspirationVideoUpload(files);
                          }}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() =>
                            inspirationVideoUploadRef.current?.click()
                          }
                          disabled={isUploadingInspirationVideo}
                        >
                          Upload Videos
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={addInspirationVideoUrl}
                          disabled={isUploadingInspirationVideo}
                        >
                          Add Video URL
                        </Button>
                      </div>
                    </>
                  )}
                </div>

                {/* Display inspiration videos */}
                {inspirationVideos.length > 0 && (
                  <div className="space-y-2">
                    <h5 className="text-sm font-medium">Inspiration Videos:</h5>
                    {inspirationVideos.map((videoItem) => (
                      <div
                        key={videoItem.id}
                        className="flex items-center justify-between p-3 bg-muted rounded-lg"
                      >
                        {videoItem.url ? (
                          // URL-based video - show editable input
                          <>
                            <div className="flex items-center gap-2 flex-1">
                              <div className="h-8 w-8 bg-purple-100 dark:bg-purple-900/20 rounded flex items-center justify-center">
                                <span className="text-xs text-purple-600 dark:text-purple-400">
                                  🎬
                                </span>
                              </div>
                              <Input
                                value={videoItem.url}
                                onChange={(e) =>
                                  updateInspirationVideo(
                                    videoItem.id,
                                    e.target.value,
                                  )
                                }
                                placeholder="https://youtube.com/... or https://tiktok.com/... or https://instagram.com/..."
                                className="text-sm"
                              />
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                removeInspirationVideo(videoItem.id)
                              }
                              className="text-red-500 hover:text-red-700 ml-2"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </>
                        ) : (
                          // Uploaded video - show like marketing videos
                          <>
                            <div
                              className="flex items-center gap-2 cursor-pointer hover:bg-muted/80 rounded p-1 -m-1 flex-1"
                              onClick={() =>
                                viewVideo(videoItem.id, videoItem.url)
                              }
                            >
                              <div className="h-8 w-8 bg-purple-100 dark:bg-purple-900/20 rounded flex items-center justify-center">
                                <span className="text-xs text-purple-600 dark:text-purple-400">
                                  🎬
                                </span>
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium">
                                    {videoItem.name}
                                  </span>
                                  <ExternalLink className="h-3 w-3 text-muted-foreground" />
                                </div>
                                {videoItem.isExisting && (
                                  <p className="text-xs text-green-600 dark:text-green-400">
                                    Current video
                                  </p>
                                )}
                                <p className="text-xs text-purple-600 dark:text-purple-400">
                                  Click to view video
                                </p>
                              </div>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                removeInspirationVideo(videoItem.id)
                              }
                              className="text-red-500 hover:text-red-700 ml-2"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Campaign Type with detailed descriptions */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Campaign Type</Label>
              <Input
                value={
                  campaign.type === "CiR"
                    ? "Creator in Residence (CiR)"
                    : campaign.type === "influencer"
                      ? "Influencer Campaign"
                      : "Challenge Campaign"
                }
                disabled
                className="bg-muted"
              />
              <div className="space-y-3 text-xs text-muted-foreground">
                {campaign.type === "CiR" && (
                  <div className="p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <p className="font-semibold text-blue-800 dark:text-blue-300 mb-2">
                      Creator in Residence (CiR):
                    </p>
                    <ul className="space-y-1 text-blue-700 dark:text-blue-400">
                      <li>
                        • Long-term partnership with creators who become brand
                        ambassadors
                      </li>
                      <li>
                        • <strong>No review process required</strong> - creators
                        can auto-track their posts using our platform
                      </li>
                      <li>
                        • Ideal for ongoing content creation and deeper brand
                        integration
                      </li>
                      <li>
                        • Streamlined workflow for established partnerships
                      </li>
                    </ul>
                  </div>
                )}

                {campaign.type === "influencer" && (
                  <div className="p-3 bg-purple-50 dark:bg-purple-950/20 rounded-lg border border-purple-200 dark:border-purple-800">
                    <p className="font-semibold text-purple-800 dark:text-purple-300 mb-2">
                      Influencer Campaign:
                    </p>
                    <ul className="space-y-1 text-purple-700 dark:text-purple-400">
                      <li>
                        • Short-term or one-off collaborations for specific
                        promotions
                      </li>
                      <li>
                        • <strong>Full approval process</strong> - creators
                        upload videos for feedback and review
                      </li>
                      <li>
                        • Built-in workflow for content approval before
                        publishing
                      </li>
                      <li>
                        • Perfect for product launches and controlled messaging
                      </li>
                    </ul>
                  </div>
                )}

                {campaign.type === "challenge" && (
                  <div className="p-3 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
                    <p className="font-semibold text-green-800 dark:text-green-300 mb-2">
                      Challenge Campaign:
                    </p>
                    <ul className="space-y-1 text-green-700 dark:text-green-400">
                      <li>
                        • Open competition format with pre-defined CPM rewards
                      </li>
                      <li>
                        • <strong>AI-verified posts</strong> - automatic
                        validation based on your requirements
                      </li>
                      <li>
                        • Creators get paid based on actual views and
                        performance
                      </li>
                      <li>
                        • Perfect for viral marketing and user-generated content
                      </li>
                    </ul>
                  </div>
                )}
              </div>
              {!canEditRestrictedFields && (
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  Campaign type cannot be changed after creators have joined or
                  the campaign has ended.
                </p>
              )}
            </div>

            {/* Video Workflow Settings */}
            <div className="space-y-3">
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  Video Review & Posting Workflow
                </Label>
                <p className="text-xs text-muted-foreground">
                  Configure how creators submit videos and post content for this
                  campaign. These settings can be changed at any time.
                </p>
              </div>

              {/* Allow Direct Posting */}
              <div className="flex items-center justify-between p-4 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
                <div className="space-y-1">
                  <div className="flex items-center space-x-2">
                    <Label
                      htmlFor="allowDirectPosting"
                      className="text-sm font-medium text-green-800 dark:text-green-300"
                    >
                      Allow Direct Posting{" "}
                      {formData.allowCreatorsToPostDirectlyToSocials
                        ? "Enabled"
                        : "Disabled"}
                    </Label>
                  </div>
                  <p className="text-xs text-green-600 dark:text-green-400">
                    Creators can upload videos directly to social media without
                    going through the video submission & feedback process.
                    <span className="block mt-1 font-medium">
                      ⚠️ Not recommended if using AI or Manual Review.
                    </span>
                  </p>
                </div>
                <Switch
                  id="allowDirectPosting"
                  checked={formData.allowCreatorsToPostDirectlyToSocials}
                  onCheckedChange={(checked) =>
                    setFormData({
                      ...formData,
                      allowCreatorsToPostDirectlyToSocials: checked,
                    })
                  }
                />
              </div>

              {/* AI Review */}
              <div className="flex items-center justify-between p-4 bg-purple-50 dark:bg-purple-950/20 rounded-lg border border-purple-200 dark:border-purple-800">
                <div className="space-y-1">
                  <div className="flex items-center space-x-2">
                    <Label
                      htmlFor="aiReview"
                      className="text-sm font-medium text-purple-800 dark:text-purple-300"
                    >
                      AI Review{" "}
                      {formData.aiReviewRequired ? "Enabled" : "Disabled"}
                    </Label>
                  </div>
                  <p className="text-xs text-purple-600 dark:text-purple-400">
                    Creators must submit videos for AI review before posting
                  </p>
                </div>
                <Switch
                  id="aiReview"
                  checked={formData.aiReviewRequired}
                  onCheckedChange={(checked) =>
                    setFormData({
                      ...formData,
                      aiReviewRequired: checked,
                    })
                  }
                />
              </div>

              {/* SuperAdmin Review (uses team review toggle) - Only visible to team members */}
              {isTeamMember && (
                <div className="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div className="space-y-1">
                    <div className="flex items-center space-x-2">
                      <Label
                        htmlFor="teamVideoReview"
                        className="text-sm font-medium text-blue-800 dark:text-blue-300"
                      >
                        SuperAdmin Review{" "}
                        {formData.teamVideoReviewRequired
                          ? "Enabled"
                          : "Disabled"}
                      </Label>
                    </div>
                    <p className="text-xs text-blue-600 dark:text-blue-400">
                      Creators must submit videos for SuperAdmin approval
                    </p>
                  </div>
                  <Switch
                    id="teamVideoReview"
                    checked={formData.teamVideoReviewRequired}
                    onCheckedChange={(checked) =>
                      setFormData({
                        ...formData,
                        teamVideoReviewRequired: checked,
                      })
                    }
                  />
                </div>
              )}

              {/* <brand> Team Review (uses manual review toggle) */}
              <div className="flex items-center justify-between p-4 bg-purple-50 dark:bg-purple-950/20 rounded-lg border border-purple-200 dark:border-purple-800">
                <div className="space-y-1">
                  <div className="flex items-center space-x-2">
                    <Label
                      htmlFor="manualVideoReview"
                      className="text-sm font-medium text-purple-800 dark:text-purple-300"
                    >
                      {brandName} Team Review{" "}
                      {formData.manualVideoReviewRequired
                        ? "Enabled"
                        : "Disabled"}
                    </Label>
                  </div>
                  <p className="text-xs text-purple-600 dark:text-purple-400">
                    Creators must submit videos for {brandName} Team approval
                    before posting
                  </p>
                </div>
                <Switch
                  id="manualVideoReview"
                  checked={formData.manualVideoReviewRequired}
                  onCheckedChange={(checked) =>
                    setFormData({
                      ...formData,
                      manualVideoReviewRequired: checked,
                    })
                  }
                />
              </div>

              {/* Info boxes based on selected options - only show ONE message */}
              {formData.teamVideoReviewRequired &&
              formData.manualVideoReviewRequired &&
              formData.aiReviewRequired &&
              formData.allowCreatorsToPostDirectlyToSocials ? (
                <div className="flex items-start space-x-2 p-3 bg-indigo-50 dark:bg-indigo-950/20 rounded-lg">
                  <Info className="h-4 w-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-indigo-700 dark:text-indigo-300">
                    <p className="font-medium mb-1">
                      Team + AI + Manual Review + Direct Posting:
                    </p>
                    <ul className="space-y-0.5">
                      <li>
                        • Creators post videos directly to social media, then
                        submit them for review
                      </li>
                      <li>
                        • Step 1: AI reviews submissions against guidelines
                      </li>
                      <li>
                        • Step 2: CreatorGrid team reviews AI-approved videos
                      </li>
                      <li>
                        • Step 3: You manually review and provide feedback on
                        team-approved videos already live on social media
                      </li>
                      <li>
                        • ⚠️ If rejected at any stage, creators must delete the
                        video from social media and repost after making changes
                      </li>
                      <li>
                        • Only fully approved videos are tracked for earnings
                      </li>
                    </ul>
                  </div>
                </div>
              ) : formData.teamVideoReviewRequired &&
                formData.manualVideoReviewRequired &&
                formData.allowCreatorsToPostDirectlyToSocials ? (
                <div className="flex items-start space-x-2 p-3 bg-indigo-50 dark:bg-indigo-950/20 rounded-lg">
                  <Info className="h-4 w-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-indigo-700 dark:text-indigo-300">
                    <p className="font-medium mb-1">
                      Team + Manual Review + Direct Posting:
                    </p>
                    <ul className="space-y-0.5">
                      <li>
                        • Creators post videos directly to social media, then
                        submit them for review
                      </li>
                      <li>• Step 1: CreatorGrid team reviews submissions</li>
                      <li>
                        • Step 2: You manually review and provide feedback on
                        team-approved videos already live on social media
                      </li>
                      <li>
                        • ⚠️ If rejected at any stage, creators must delete the
                        video from social media and repost after making changes
                      </li>
                      <li>• Only approved videos are tracked for earnings</li>
                    </ul>
                  </div>
                </div>
              ) : formData.teamVideoReviewRequired &&
                formData.aiReviewRequired &&
                formData.allowCreatorsToPostDirectlyToSocials ? (
                <div className="flex items-start space-x-2 p-3 bg-indigo-50 dark:bg-indigo-950/20 rounded-lg">
                  <Info className="h-4 w-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-indigo-700 dark:text-indigo-300">
                    <p className="font-medium mb-1">
                      Team + AI Review + Direct Posting:
                    </p>
                    <ul className="space-y-0.5">
                      <li>
                        • Creators post videos directly to social media, then
                        submit them for review
                      </li>
                      <li>
                        • Step 1: AI automatically checks submissions against
                        your campaign guidelines
                      </li>
                      <li>
                        • Step 2: CreatorGrid team reviews AI-approved videos
                      </li>
                      <li>
                        • ⚠️ If rejected at any stage, creators must delete the
                        video from social media and repost after making changes
                      </li>
                      <li>• Only approved videos are tracked for earnings</li>
                    </ul>
                  </div>
                </div>
              ) : formData.teamVideoReviewRequired &&
                formData.manualVideoReviewRequired &&
                formData.aiReviewRequired ? (
                <div className="flex items-start space-x-2 p-3 bg-indigo-50 dark:bg-indigo-950/20 rounded-lg">
                  <Info className="h-4 w-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-indigo-700 dark:text-indigo-300">
                    <p className="font-medium mb-1">
                      Team + AI + Manual Review Process:
                    </p>
                    <ul className="space-y-0.5">
                      <li>
                        • Creators must submit videos for review before posting
                        to social media
                      </li>
                      <li>
                        • Step 1: AI automatically reviews against guidelines
                      </li>
                      <li>
                        • Step 2: CreatorGrid team reviews AI-approved videos
                      </li>
                      <li>
                        • Step 3: You manually review and approve all
                        team-approved videos
                      </li>
                      <li>
                        • Only videos that pass all three reviews can be posted
                        and tracked for earnings
                      </li>
                      <li>
                        • Combines automated efficiency with team and human
                        oversight for maximum quality control
                      </li>
                    </ul>
                  </div>
                </div>
              ) : formData.teamVideoReviewRequired &&
                formData.manualVideoReviewRequired ? (
                <div className="flex items-start space-x-2 p-3 bg-indigo-50 dark:bg-indigo-950/20 rounded-lg">
                  <Info className="h-4 w-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-indigo-700 dark:text-indigo-300">
                    <p className="font-medium mb-1">
                      Team + Manual Review Process:
                    </p>
                    <ul className="space-y-0.5">
                      <li>
                        • Creators must submit videos for review before posting
                        to social media
                      </li>
                      <li>
                        • Step 1: CreatorGrid team reviews all submissions
                      </li>
                      <li>
                        • Step 2: You manually review and approve all
                        team-approved videos
                      </li>
                      <li>
                        • Only videos that pass both reviews can be posted and
                        tracked for earnings
                      </li>
                      <li>
                        • Combines team oversight with human control for quality
                        assurance
                      </li>
                    </ul>
                  </div>
                </div>
              ) : formData.teamVideoReviewRequired &&
                formData.aiReviewRequired ? (
                <div className="flex items-start space-x-2 p-3 bg-indigo-50 dark:bg-indigo-950/20 rounded-lg">
                  <Info className="h-4 w-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-indigo-700 dark:text-indigo-300">
                    <p className="font-medium mb-1">
                      Team + AI Review Process:
                    </p>
                    <ul className="space-y-0.5">
                      <li>
                        • Creators must submit videos for review before posting
                        to social media
                      </li>
                      <li>
                        • Step 1: AI automatically reviews against guidelines
                      </li>
                      <li>
                        • Step 2: CreatorGrid team reviews AI-approved videos
                      </li>
                      <li>
                        • Only videos that pass both reviews can be posted and
                        tracked for earnings
                      </li>
                      <li>
                        • Combines automated efficiency with team oversight for
                        quality control
                      </li>
                    </ul>
                  </div>
                </div>
              ) : formData.teamVideoReviewRequired ? (
                <div className="flex items-start space-x-2 p-3 bg-indigo-50 dark:bg-indigo-950/20 rounded-lg">
                  <Info className="h-4 w-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-indigo-700 dark:text-indigo-300">
                    <p className="font-medium mb-1">Team Review Process:</p>
                    <ul className="space-y-0.5">
                      <li>
                        • Creators must submit videos for CreatorGrid team
                        approval before posting to social media
                      </li>
                      <li>
                        • CreatorGrid team reviews and provides feedback on all
                        submissions
                      </li>
                      <li>
                        • Only team-approved videos can be posted and tracked
                        for earnings
                      </li>
                      <li>
                        • Perfect for maintaining high quality standards with
                        team oversight
                      </li>
                    </ul>
                  </div>
                </div>
              ) : formData.manualVideoReviewRequired &&
                formData.aiReviewRequired &&
                formData.allowCreatorsToPostDirectlyToSocials ? (
                <div className="flex items-start space-x-2 p-3 bg-amber-50 dark:bg-amber-950/20 rounded-lg">
                  <Info className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-amber-700 dark:text-amber-300">
                    <p className="font-medium mb-1">
                      AI + Manual Review + Direct Posting:
                    </p>
                    <ul className="space-y-0.5">
                      <li>
                        • Creators post videos directly to social media, then
                        submit them for review
                      </li>
                      <li>
                        • Step 1: AI reviews submissions against guidelines
                      </li>
                      <li>
                        • Step 2: You manually review and provide feedback on
                        videos already live on social media
                      </li>
                      <li>
                        • ⚠️ If rejected at any stage, creators must delete the
                        video from social media and repost after making changes
                      </li>
                      <li>
                        • Only fully approved videos are tracked for earnings
                      </li>
                    </ul>
                  </div>
                </div>
              ) : formData.manualVideoReviewRequired &&
                formData.allowCreatorsToPostDirectlyToSocials ? (
                <div className="flex items-start space-x-2 p-3 bg-amber-50 dark:bg-amber-950/20 rounded-lg">
                  <Info className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-amber-700 dark:text-amber-300">
                    <p className="font-medium mb-1">
                      Manual Review + Direct Posting:
                    </p>
                    <ul className="space-y-0.5">
                      <li>
                        • Creators post videos directly to social media, then
                        submit them for your review
                      </li>
                      <li>
                        • You review and provide feedback on videos already live
                        on social media
                      </li>
                      <li>
                        • ⚠️ If rejected, creators must delete the video from
                        social media and repost after making changes
                      </li>
                      <li>• Only approved videos are tracked for earnings</li>
                    </ul>
                  </div>
                </div>
              ) : formData.aiReviewRequired &&
                formData.allowCreatorsToPostDirectlyToSocials ? (
                <div className="flex items-start space-x-2 p-3 bg-purple-50 dark:bg-purple-950/20 rounded-lg">
                  <Info className="h-4 w-4 text-purple-500 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-purple-700 dark:text-purple-300">
                    <p className="font-medium mb-1">
                      AI Review + Direct Posting:
                    </p>
                    <ul className="space-y-0.5">
                      <li>
                        • Creators post videos directly to social media, then
                        submit them for AI review
                      </li>
                      <li>
                        • AI automatically checks submissions against your
                        campaign guidelines
                      </li>
                      <li>
                        • ⚠️ If rejected, creators must delete the video from
                        social media and repost after making changes
                      </li>
                      <li>• Only approved videos are tracked for earnings</li>
                    </ul>
                  </div>
                </div>
              ) : formData.manualVideoReviewRequired &&
                formData.aiReviewRequired ? (
                <div className="flex items-start space-x-2 p-3 bg-indigo-50 dark:bg-indigo-950/20 rounded-lg">
                  <Info className="h-4 w-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-indigo-700 dark:text-indigo-300">
                    <p className="font-medium mb-1">
                      AI + Manual Review Process:
                    </p>
                    <ul className="space-y-0.5">
                      <li>
                        • Creators must submit videos for review before posting
                        to social media
                      </li>
                      <li>
                        • Step 1: AI automatically reviews against guidelines
                      </li>
                      <li>
                        • Step 2: You manually review and approve all
                        AI-approved videos
                      </li>
                      <li>
                        • Only videos that pass both reviews can be posted and
                        tracked for earnings
                      </li>
                      <li>
                        • Combines automated efficiency with human oversight for
                        maximum quality control
                      </li>
                    </ul>
                  </div>
                </div>
              ) : formData.manualVideoReviewRequired ? (
                <div className="flex items-start space-x-2 p-3 bg-amber-50 dark:bg-amber-950/20 rounded-lg">
                  <Info className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-amber-700 dark:text-amber-300">
                    <p className="font-medium mb-1">Manual Review Process:</p>
                    <ul className="space-y-0.5">
                      <li>
                        • Creators must submit videos for your approval before
                        posting to social media
                      </li>
                      <li>
                        • You review and provide feedback on all submissions
                      </li>
                      <li>
                        • Only approved videos can be posted and tracked for
                        earnings
                      </li>
                      <li>
                        • Perfect for maintaining brand quality and messaging
                      </li>
                    </ul>
                  </div>
                </div>
              ) : formData.aiReviewRequired ? (
                <div className="flex items-start space-x-2 p-3 bg-purple-50 dark:bg-purple-950/20 rounded-lg">
                  <Info className="h-4 w-4 text-purple-500 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-purple-700 dark:text-purple-300">
                    <p className="font-medium mb-1">AI Review Process:</p>
                    <ul className="space-y-0.5">
                      <li>
                        • Creators must submit videos for AI review before
                        posting to social media
                      </li>
                      <li>
                        • AI automatically checks submissions against your
                        campaign guidelines
                      </li>
                      <li>
                        • Provides automated feedback on content quality and
                        compliance
                      </li>
                      <li>
                        • Speeds up the review process while maintaining quality
                        standards
                      </li>
                    </ul>
                  </div>
                </div>
              ) : formData.allowCreatorsToPostDirectlyToSocials ? (
                <div className="flex items-start space-x-2 p-3 bg-green-50 dark:bg-green-950/20 rounded-lg">
                  <Info className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-green-700 dark:text-green-300">
                    <p className="font-medium mb-1">Direct Posting Enabled:</p>
                    <ul className="space-y-0.5">
                      <li>
                        • Creators can upload videos directly to social media
                        without submitting for review
                      </li>
                      <li>• No review or approval process required</li>
                      <li>
                        • Videos are automatically tracked via social media
                        platform APIs after posting
                      </li>
                      <li>
                        • Best for trusted partnerships and streamlined
                        workflows
                      </li>
                    </ul>
                  </div>
                </div>
              ) : null}
            </div>

            {/* Campaign Budget */}
            <div className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="budget" className="text-sm font-medium">
                  Campaign Budget (Optional)
                </Label>
                <p className="text-xs text-muted-foreground">
                  {canEditRestrictedFields
                    ? "Set a total budget cap for your campaign. Once approved video earnings reach this amount, creators will no longer be able to post new content and video views will be frozen."
                    : "Shows the total budget cap for this campaign."}
                </p>
              </div>
              <div className="space-y-2">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-primary/60">
                    $
                  </span>
                  <Input
                    id="budget"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.budget || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        budget: e.target.value
                          ? parseFloat(e.target.value)
                          : undefined,
                      })
                    }
                    placeholder="1000.00"
                    className="w-full pl-8"
                    disabled={!canEditRestrictedFields}
                  />
                </div>
              </div>

              {/* Budget Estimation */}
              {formData.budget &&
                formData.budget > 0 &&
                (() => {
                  // Check if we have specific CPM values set
                  const hasPredefinedCpm =
                    formData.predefinedCpm && formData.predefinedCpm > 0;
                  const hasCpmRange =
                    (formData.budgetCpmMin && formData.budgetCpmMin > 0) ||
                    (formData.budgetCpmMax && formData.budgetCpmMax > 0);

                  return (
                    <div className="p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                      <h5 className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-3">
                        Budget Estimation - Views Coverage
                      </h5>

                      {hasPredefinedCpm ? (
                        // Show estimation based on predefined CPM (challenge campaigns)
                        <div>
                          <p className="text-xs text-blue-700 dark:text-blue-400 mb-3">
                            With a ${formData.budget.toLocaleString()} budget at
                            ${formData.predefinedCpm} CPM:
                          </p>
                          <div className="p-3 bg-white dark:bg-gray-800 rounded border">
                            <div className="text-lg font-semibold text-blue-800 dark:text-blue-300">
                              ~
                              {Math.floor(
                                ((formData.budget || 0) * 1000) /
                                  (formData.predefinedCpm || 1),
                              ).toLocaleString()}{" "}
                              total views
                            </div>
                            <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                              This is how many views your entire budget will
                              cover across all creators
                            </div>
                          </div>
                        </div>
                      ) : hasCpmRange ? (
                        // Show estimation based on CPM range
                        <div>
                          <p className="text-xs text-blue-700 dark:text-blue-400 mb-3">
                            With a ${formData.budget.toLocaleString()} budget
                            using your CPM range:
                          </p>
                          <div className="grid grid-cols-1 gap-2">
                            {formData.budgetCpmMin &&
                              formData.budgetCpmMin > 0 && (
                                <div className="p-2 bg-white dark:bg-gray-800 rounded border">
                                  <div className="font-medium text-blue-800 dark:text-blue-300">
                                    At Min CPM (${formData.budgetCpmMin})
                                  </div>
                                  <div className="text-blue-600 dark:text-blue-400">
                                    ~
                                    {Math.floor(
                                      ((formData.budget || 0) * 1000) /
                                        formData.budgetCpmMin,
                                    ).toLocaleString()}{" "}
                                    views
                                  </div>
                                </div>
                              )}
                            {formData.budgetCpmMax &&
                              formData.budgetCpmMax > 0 && (
                                <div className="p-2 bg-white dark:bg-gray-800 rounded border">
                                  <div className="font-medium text-blue-800 dark:text-blue-300">
                                    At Max CPM (${formData.budgetCpmMax})
                                  </div>
                                  <div className="text-blue-600 dark:text-blue-400">
                                    ~
                                    {Math.floor(
                                      ((formData.budget || 0) * 1000) /
                                        formData.budgetCpmMax,
                                    ).toLocaleString()}{" "}
                                    views
                                  </div>
                                </div>
                              )}
                          </div>
                        </div>
                      ) : (
                        // Show multiple CPM scenarios when no specific CPM is set
                        <div>
                          <p className="text-xs text-blue-700 dark:text-blue-400 mb-3">
                            With a ${formData.budget.toLocaleString()} budget,
                            here's approximately how many total views you could
                            get:
                          </p>
                          <div className="grid grid-cols-2 gap-3">
                            {[
                              { cpm: 1.0, label: "Low CPM ($1.00)" },
                              { cpm: 3.0, label: "Average CPM ($3.00)" },
                              { cpm: 5.0, label: "Good CPM ($5.00)" },
                              { cpm: 10.0, label: "High CPM ($10.00)" },
                            ].map(({ cpm, label }) => {
                              const totalViews = Math.floor(
                                ((formData.budget || 0) * 1000) / cpm,
                              );
                              return (
                                <div
                                  key={cpm}
                                  className="p-2 bg-white dark:bg-gray-800 rounded border text-xs"
                                >
                                  <div className="font-medium text-blue-800 dark:text-blue-300">
                                    {label}
                                  </div>
                                  <div className="text-blue-600 dark:text-blue-400">
                                    ~{totalViews.toLocaleString()} views
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                            💡 Higher CPM = fewer total views but potentially
                            better targeting/quality
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })()}

              <div className="flex items-start space-x-2 p-3 bg-amber-50 dark:bg-amber-950/20 rounded-lg">
                <Info className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-amber-700 dark:text-amber-300">
                  <p className="font-medium mb-1">How Budget Works:</p>
                  <ul className="space-y-0.5">
                    <li>• Creators can see your total campaign budget</li>
                    <li>
                      • Each approved video's earnings count toward this budget
                    </li>
                    <li>
                      • When earnings reach the budget limit, new posts are
                      disabled
                    </li>
                    <li>
                      • Video views are frozen to prevent further earnings
                    </li>
                    <li>• Leave empty for unlimited campaign budget</li>
                  </ul>
                </div>
              </div>
              {!canEditRestrictedFields && (
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  Campaign budget cannot be changed after creators have joined
                  or the campaign has ended.
                </p>
              )}
            </div>

            {/* Creator Brief */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="creatorBrief" className="text-sm font-medium">
                  Creator Brief (Optional)
                </Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setFormData({
                      ...formData,
                      creatorBrief: getTemplateBrief(formData.type),
                    })
                  }
                  className="text-xs"
                >
                  Use Template
                </Button>
              </div>
              <RichTextEditor
                value={formData.creatorBrief || ""}
                onChange={(value) =>
                  setFormData({ ...formData, creatorBrief: value })
                }
                placeholder="Describe what you're looking for in creators and content..."
                className="min-h-[150px] font-mono text-sm"
              />
            </div>

            {/* What Problems Does Your Product Solve */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="problemsSolved" className="text-sm font-medium">
                  What Problems Does Your Product Solve (Optional)
                </Label>
                {isAutoSaving && (
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Auto-saving...
                  </div>
                )}
              </div>
              <RichTextEditor
                value={formData.problemsSolved || ""}
                onChange={(value) =>
                  setFormData({ ...formData, problemsSolved: value })
                }
                placeholder="Describe the key problems your product solves for users..."
                className="min-h-[100px]"
              />
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">
                  Help creators understand the value proposition of your product
                  and what pain points it addresses for users.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setAiScrapingDialogType("usps");
                    setAiScrapingDialogOpen(true);
                  }}
                  disabled={isAiScraping}
                  className="text-xs"
                >
                  {isAiScraping ? (
                    <>
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    "Let AI help"
                  )}
                </Button>
              </div>
            </div>

            {/* Customer Testimonials */}
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">
                    Customer Testimonials (Optional)
                  </Label>
                  {isAutoSaving && (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Auto-saving...
                    </div>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Add customer testimonials to provide social proof. Each
                  testimonial should include the customer's feedback and their
                  name.
                </p>
              </div>

              <div className="flex items-center space-x-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addTestimonial}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Testimonial
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setAiScrapingDialogType("testimonials");
                    setAiScrapingDialogOpen(true);
                  }}
                  disabled={isAiScraping}
                  className="text-xs"
                >
                  {isAiScraping ? (
                    <>
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    "Let AI help"
                  )}
                </Button>
              </div>

              {formData.customerTestimonials &&
                formData.customerTestimonials.length > 0 && (
                  <div className="space-y-3">
                    {formData.customerTestimonials.map((testimonial, index) => (
                      <div
                        key={index}
                        className="p-4 border rounded-lg space-y-3"
                      >
                        <div className="flex items-center justify-between">
                          <Label className="text-sm font-medium">
                            Testimonial {index + 1}
                          </Label>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeTestimonial(index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>

                        <div className="space-y-2">
                          <Label className="text-xs text-muted-foreground">
                            Customer Feedback *
                          </Label>
                          <RichTextEditor
                            value={testimonial.testimonial}
                            onChange={(value) =>
                              updateTestimonial(index, "testimonial", value)
                            }
                            placeholder="e.g., 'This app completely transformed how I manage my daily tasks. I can't imagine working without it!'"
                            className="w-full min-h-[80px]"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label className="text-xs text-muted-foreground">
                            Customer Name *
                          </Label>
                          <Input
                            type="text"
                            value={testimonial.customerName}
                            onChange={(e) =>
                              updateTestimonial(
                                index,
                                "customerName",
                                e.target.value,
                              )
                            }
                            placeholder="e.g., Sarah Johnson, Marketing Manager"
                            className="w-full"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

              <div className="flex items-start space-x-2 p-3 bg-green-50 dark:bg-green-950/20 rounded-lg">
                <Info className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-green-700 dark:text-green-300">
                  <p className="font-medium mb-1">Testimonial Tips:</p>
                  <ul className="space-y-0.5">
                    <li>
                      • Include specific benefits or results customers
                      experienced
                    </li>
                    <li>• Use authentic, conversational language</li>
                    <li>
                      • Include customer's name and role/title if possible
                    </li>
                    <li>• Keep testimonials concise but impactful</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Creator Criteria - Only show when applications are required */}
            {requiresApplications() && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    Creator Evaluation Criteria
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    {canEditRestrictedFields
                      ? "Define specific criteria to evaluate creator applications. These will be used by AI to score and rank applicants."
                      : "Shows the criteria used to evaluate creator applications for this campaign."}
                  </p>
                </div>

                {canEditRestrictedFields && (
                  <div className="flex items-center space-x-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addCriterion}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Criterion
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={useTemplateCriteria}
                    >
                      Use Template
                    </Button>
                  </div>
                )}

                {formData.creatorCriteria &&
                  formData.creatorCriteria.length > 0 && (
                    <div className="space-y-3">
                      {formData.creatorCriteria.map((criterion, index) => (
                        <div
                          key={index}
                          className="p-4 border rounded-lg space-y-3"
                        >
                          <div className="flex items-center justify-between">
                            <Label className="text-sm font-medium">
                              Criterion {index + 1}
                            </Label>
                            {canEditRestrictedFields && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeCriterion(index)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            )}
                          </div>

                          <div className="space-y-2">
                            <Label className="text-xs text-muted-foreground">
                              Title *
                            </Label>
                            <Input
                              type="text"
                              value={criterion.title}
                              onChange={(e) =>
                                updateCriterion(index, "title", e.target.value)
                              }
                              placeholder="e.g., Content Quality & Production Value"
                              className="w-full"
                              disabled={!canEditRestrictedFields}
                            />
                          </div>

                          <div className="space-y-2">
                            <Label className="text-xs text-muted-foreground">
                              Description *
                            </Label>
                            <Textarea
                              value={criterion.description}
                              onChange={(e) =>
                                updateCriterion(
                                  index,
                                  "description",
                                  e.target.value,
                                )
                              }
                              placeholder="Describe what this criterion evaluates..."
                              className="w-full min-h-[80px]"
                              rows={3}
                              disabled={!canEditRestrictedFields}
                            />
                          </div>

                          <div className="space-y-2">
                            <Label className="text-xs text-muted-foreground">
                              Weight (Optional)
                            </Label>
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              value={criterion.weight || ""}
                              onChange={(e) =>
                                updateCriterion(
                                  index,
                                  "weight",
                                  e.target.value
                                    ? parseInt(e.target.value)
                                    : "",
                                )
                              }
                              placeholder="0-100"
                              className="w-full"
                              disabled={!canEditRestrictedFields}
                            />
                            <p className="text-xs text-muted-foreground">
                              Percentage weight for this criterion (leave empty
                              for equal weighting)
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                <div className="flex items-start space-x-2 p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg">
                  <Info className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-blue-700 dark:text-blue-300">
                    <p className="font-medium mb-1">How it works:</p>
                    <p>
                      AI will evaluate each creator application against these
                      criteria and provide scores. This helps you identify the
                      best candidates for your campaign efficiently.
                    </p>
                  </div>
                </div>
                {!canEditRestrictedFields && (
                  <p className="text-xs text-amber-600 dark:text-amber-400">
                    Creator criteria cannot be changed after creators have
                    joined or the campaign has ended.
                  </p>
                )}
              </div>
            )}

            {/* Challenge-specific fields */}
            {formData.type === "challenge" && (
              <>
                {/* CPM Reward */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">
                      CPM Reward (Cost Per 1,000 Views) *
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      {canEditRestrictedFields
                        ? "Set the CPM reward creators will earn for every 1,000 views on their content."
                        : "Shows the CPM reward creators earn for every 1,000 views on their content."}
                    </p>
                  </div>

                  <div className="flex gap-2">
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.predefinedCpm || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          predefinedCpm: e.target.value
                            ? parseFloat(e.target.value)
                            : undefined,
                        })
                      }
                      placeholder="2.00"
                      className="flex-1"
                      disabled={!canEditRestrictedFields}
                    />
                    {canEditRestrictedFields && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowCpmCalculator(!showCpmCalculator)}
                        className="whitespace-nowrap"
                      >
                        Calculator
                      </Button>
                    )}
                  </div>

                  {showCpmCalculator && canEditRestrictedFields && (
                    <div className="p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-blue-800 dark:text-blue-300">
                          CPM Calculator
                        </h4>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                            </TooltipTrigger>
                            <TooltipContent
                              side="right"
                              className="max-w-[400px]"
                            >
                              Formula: 1000 × (Views-to-download %) ×
                              (Download-to-paid %) × (LTV × CAC:LTV ratio)
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <Label className="text-xs text-blue-700 dark:text-blue-400">
                            Lifetime Revenue per User ($)
                          </Label>
                          <Input
                            type="number"
                            value={cpmCalculator.lifetimeRevenue}
                            onChange={(e) =>
                              setCpmCalculator({
                                ...cpmCalculator,
                                lifetimeRevenue: e.target.value,
                              })
                            }
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-blue-700 dark:text-blue-400">
                            CAC:LTV Ratio
                          </Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={cpmCalculator.cacLtvRatio}
                            onChange={(e) =>
                              setCpmCalculator({
                                ...cpmCalculator,
                                cacLtvRatio: e.target.value,
                              })
                            }
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-blue-700 dark:text-blue-400">
                            Views-to-download conversion (%)
                          </Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={cpmCalculator.viewsToDownload}
                            onChange={(e) =>
                              setCpmCalculator({
                                ...cpmCalculator,
                                viewsToDownload: e.target.value,
                              })
                            }
                            className="mt-1"
                            placeholder="0.25"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-blue-700 dark:text-blue-400">
                            Download-to-paid conversion (%)
                          </Label>
                          <Input
                            type="number"
                            value={cpmCalculator.downloadToPaid}
                            onChange={(e) =>
                              setCpmCalculator({
                                ...cpmCalculator,
                                downloadToPaid: e.target.value,
                              })
                            }
                            className="mt-1"
                          />
                        </div>
                      </div>
                      <div className="p-3 bg-white dark:bg-gray-800 rounded border">
                        <p className="text-sm font-medium text-blue-800 dark:text-blue-300">
                          Recommended CPM: $
                          {isNaN(calculateRecommendedCpm())
                            ? "0.00"
                            : calculateRecommendedCpm().toFixed(2)}
                        </p>
                        <Button
                          type="button"
                          size="sm"
                          onClick={() =>
                            setFormData({
                              ...formData,
                              predefinedCpm: parseFloat(
                                calculateRecommendedCpm().toFixed(2),
                              ),
                            })
                          }
                          className="mt-2"
                          disabled={isNaN(calculateRecommendedCpm())}
                        >
                          Use This CPM
                        </Button>
                      </div>
                    </div>
                  )}

                  <div className="flex items-start space-x-2 p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg">
                    <Info className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                    <div className="text-xs text-blue-700 dark:text-blue-300">
                      <p className="font-medium mb-1">Challenge CPM Example:</p>
                      <p>
                        A $2 CPM means creators earn $2 for every 1,000 views.
                        If their video gets 50,000 views, they earn $100 ($2 ×
                        50).
                      </p>
                    </div>
                  </div>
                  {!canEditRestrictedFields && (
                    <p className="text-xs text-amber-600 dark:text-amber-400">
                      CPM reward cannot be changed after creators have joined or
                      the campaign has ended.
                    </p>
                  )}
                </div>

                {/* Validation Requirements */}
                <div className="space-y-2">
                  <Label
                    htmlFor="validationRequirements"
                    className="text-sm font-medium"
                  >
                    Post Validation Requirements *
                  </Label>
                  <RichTextEditor
                    value={formData.validationRequirements || ""}
                    onChange={(value) =>
                      setFormData({
                        ...formData,
                        validationRequirements: value,
                      })
                    }
                    placeholder="e.g., At least one app screenshot with our company logo visible in it"
                    className="min-h-[100px]"
                    readOnly={!canEditRestrictedFields}
                  />
                  <p className="text-xs text-muted-foreground">
                    {canEditRestrictedFields
                      ? "Specify what makes a post valid for your challenge. Our AI will automatically verify each post against these requirements. Creators can challenge the AI decision for manual review."
                      : "Shows the requirements creators must meet when posting their content for this challenge."}
                  </p>
                  {!canEditRestrictedFields && (
                    <p className="text-xs text-amber-600 dark:text-amber-400">
                      Validation requirements cannot be changed after creators
                      have joined or the campaign has ended.
                    </p>
                  )}
                </div>
              </>
            )}

            {/* Public/Private Toggle */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Campaign Visibility</Label>
              {formData.type === "challenge" ? (
                <Select
                  value={
                    formData.isPublic
                      ? formData.applicationNotRequired
                        ? "public"
                        : "application-only"
                      : "private"
                  }
                  onValueChange={(value: string) => {
                    if (value === "public") {
                      setFormData({
                        ...formData,
                        isPublic: true,
                        applicationNotRequired: true,
                      });
                    } else if (value === "application-only") {
                      setFormData({
                        ...formData,
                        isPublic: true,
                        applicationNotRequired: false,
                      });
                    } else {
                      setFormData({
                        ...formData,
                        isPublic: false,
                        applicationNotRequired: false,
                      });
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select visibility" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="public">Public (Open to All)</SelectItem>
                    <SelectItem value="application-only">
                      Application Only
                    </SelectItem>
                    <SelectItem value="private">Private</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Select
                  value={formData.isPublic ? "public" : "private"}
                  onValueChange={(value: string) =>
                    setFormData({ ...formData, isPublic: value === "public" })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select visibility" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="public">Public</SelectItem>
                    <SelectItem value="private">Private</SelectItem>
                  </SelectContent>
                </Select>
              )}
              <div className="space-y-1 text-xs text-muted-foreground">
                {formData.type === "challenge" ? (
                  <>
                    <p>
                      <strong>Public (Open to All):</strong> Anyone can
                      participate immediately without approval
                    </p>
                    <p>
                      <strong>Application Only:</strong> Visible to all, but
                      creators must apply and be approved
                    </p>
                    <p>
                      <strong>Private:</strong> Invitation-only and not visible
                      to creators
                    </p>
                  </>
                ) : (
                  <>
                    <p>
                      <strong>Public:</strong> Can be discovered and applied to
                      by all creators
                    </p>
                    <p>
                      <strong>Private:</strong> Invitation-only and not visible
                      to creators
                    </p>
                  </>
                )}
              </div>
            </div>

            {/* End Date */}
            <div className="space-y-2">
              <Label htmlFor="endDate" className="text-sm font-medium">
                End Date (Optional)
              </Label>
              <Input
                id="endDate"
                type="date"
                value={endDateInput}
                onChange={(e) => setEndDateInput(e.target.value)}
                min={new Date().toISOString().split("T")[0]}
              />
            </div>

            {/* Allowed Channels */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Allowed Platforms</Label>
              <p className="text-xs text-muted-foreground">
                Select which platforms creators can use for this campaign. All
                platforms are selected by default.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {(
                  [
                    { key: "instagram", label: "Instagram" },
                    { key: "tiktok", label: "TikTok" },
                    { key: "youtube-shorts", label: "YouTube Shorts" },
                    { key: "youtube", label: "YouTube Long Form" },
                  ] as const
                ).map((platform) => (
                  <label
                    key={platform.key}
                    className="flex items-center space-x-2 text-sm"
                  >
                    <input
                      type="checkbox"
                      checked={
                        formData.allowedChannels?.includes(platform.key) ??
                        false
                      }
                      onChange={(e) => {
                        const currentChannels = formData.allowedChannels || [];
                        if (e.target.checked) {
                          setFormData({
                            ...formData,
                            allowedChannels: [...currentChannels, platform.key],
                          });
                        } else {
                          setFormData({
                            ...formData,
                            allowedChannels: currentChannels.filter(
                              (channel) => channel !== platform.key,
                            ),
                          });
                        }
                      }}
                      className="rounded border-gray-300"
                      disabled={!canEditRestrictedFields}
                    />
                    <span>{platform.label}</span>
                  </label>
                ))}
              </div>
              {!canEditRestrictedFields && (
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  Allowed platforms cannot be changed after creators have joined
                  or the campaign has ended.
                </p>
              )}
            </div>

            {/* Budget Range - Only for non-challenge campaigns */}
            {formData.type !== "challenge" && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    Budget Range (Optional)
                  </Label>
                </div>

                {/* CPM Budget */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium text-blue-700 dark:text-blue-300">
                    CPM Budget (Cost Per 1,000 Views)
                  </Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="budgetCpmMin" className="text-sm">
                        Minimum CPM ($)
                      </Label>
                      <Input
                        id="budgetCpmMin"
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.budgetCpmMin || ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            budgetCpmMin: e.target.value
                              ? parseFloat(e.target.value)
                              : undefined,
                          })
                        }
                        placeholder="0.00"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="budgetCpmMax" className="text-sm">
                        Maximum CPM ($)
                      </Label>
                      <Input
                        id="budgetCpmMax"
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.budgetCpmMax || ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            budgetCpmMax: e.target.value
                              ? parseFloat(e.target.value)
                              : undefined,
                          })
                        }
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                </div>

                {/* Flat Fee Budget */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium text-green-700 dark:text-green-300">
                    Flat Fee Budget (Per Video/Content)
                  </Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="budgetFlatFeeMin" className="text-sm">
                        Minimum Flat Fee ($)
                      </Label>
                      <Input
                        id="budgetFlatFeeMin"
                        type="number"
                        step="1"
                        min="0"
                        value={formData.budgetFlatFeeMin || ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            budgetFlatFeeMin: e.target.value
                              ? parseFloat(e.target.value)
                              : undefined,
                          })
                        }
                        placeholder="0"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="budgetFlatFeeMax" className="text-sm">
                        Maximum Flat Fee ($)
                      </Label>
                      <Input
                        id="budgetFlatFeeMax"
                        type="number"
                        step="1"
                        min="0"
                        value={formData.budgetFlatFeeMax || ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            budgetFlatFeeMax: e.target.value
                              ? parseFloat(e.target.value)
                              : undefined,
                          })
                        }
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Eligible Countries Selection */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  Creator Eligibility by Country
                </Label>
                <p className="text-xs text-muted-foreground">
                  {canEditRestrictedFields
                    ? "Select which countries' creators are eligible to participate in this campaign."
                    : "Shows which countries' creators are eligible to participate in this campaign."}
                </p>
              </div>

              <div className="p-4 border border-border rounded-lg bg-muted/20">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h5 className="text-sm font-medium text-primary">
                      Eligible Countries (
                      {formData.eligibleCountries?.length || 0} of{" "}
                      {APPROVED_STRIPE_CONNECT_COUNTRIES.length} selected)
                    </h5>
                    {canEditRestrictedFields && (
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setFormData({
                              ...formData,
                              eligibleCountries:
                                APPROVED_STRIPE_CONNECT_COUNTRIES,
                            })
                          }
                          className="text-xs"
                        >
                          Select All
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setFormData({
                              ...formData,
                              eligibleCountries: [],
                            })
                          }
                          className="text-xs"
                        >
                          Clear All
                        </Button>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-64 overflow-y-auto">
                    {Object.entries(
                      countries as { [code: string]: { name: string } },
                    )
                      .filter(([code]) =>
                        APPROVED_STRIPE_CONNECT_COUNTRIES.includes(code),
                      )
                      .sort(([, a], [, b]) => a.name.localeCompare(b.name))
                      .map(([code, country]) => (
                        <label
                          key={code}
                          className={`flex items-center gap-2 p-2 rounded ${
                            canEditRestrictedFields
                              ? "hover:bg-background/50 cursor-pointer"
                              : "cursor-default"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={
                              formData.eligibleCountries?.includes(code) ||
                              false
                            }
                            onChange={(e) => {
                              if (!canEditRestrictedFields) return;
                              const currentCountries =
                                formData.eligibleCountries || [];
                              if (e.target.checked) {
                                setFormData({
                                  ...formData,
                                  eligibleCountries: [
                                    ...currentCountries,
                                    code,
                                  ],
                                });
                              } else {
                                setFormData({
                                  ...formData,
                                  eligibleCountries: currentCountries.filter(
                                    (c) => c !== code,
                                  ),
                                });
                              }
                            }}
                            disabled={!canEditRestrictedFields}
                            className="rounded border-gray-300"
                          />
                          <span className="text-sm">
                            {getUnicodeFlagIcon(code)}
                          </span>
                          <span className="text-xs font-medium truncate">
                            {country.name}
                          </span>
                        </label>
                      ))}
                  </div>

                  <div className="flex items-start space-x-2 p-2 bg-blue-50 dark:bg-blue-950/20 rounded">
                    <Info className="h-3 w-3 text-blue-500 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-blue-700 dark:text-blue-300">
                      Only creators with Stripe Connect accounts from selected
                      countries will be able to apply and participate in this
                      campaign. This ensures proper payment processing.
                    </p>
                  </div>
                  {!canEditRestrictedFields && (
                    <p className="text-xs text-amber-600 dark:text-amber-400">
                      Eligible countries cannot be changed after creators have
                      joined or the campaign has ended.
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-between pt-4">
              <Button
                type="button"
                variant="destructive"
                onClick={() => setShowEndCampaignDialog(true)}
                disabled={campaign.status === "ended"}
                className="flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                {campaign.status === "ended"
                  ? "Campaign Ended"
                  : "End Campaign"}
              </Button>

              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isUpdating}>
                  {isUpdating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    "Update Campaign"
                  )}
                </Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showEndCampaignDialog}
        onOpenChange={setShowEndCampaignDialog}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>End Campaign</DialogTitle>
            <DialogDescription>
              Are you sure you want to end "{campaign.name}"? This action cannot
              be undone. All active contracts must be completed before ending a
              campaign.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => setShowEndCampaignDialog(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleEndCampaign}
              disabled={isEnding}
            >
              {isEnding ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Ending...
                </>
              ) : (
                "End Campaign"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AiScrapingDialog
        open={aiScrapingDialogOpen}
        onOpenChange={setAiScrapingDialogOpen}
        type={aiScrapingDialogType}
        onSubmit={handleAiScraping}
        isLoading={isAiScraping}
      />
    </>
  );
}
